import React, { useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import omega from '../../assets/images/model/omega (italian).svg';
import omegaGray from '../../assets/images/model/omega-grey.svg';

// Utility function to insert special characters
const insertSpecialCharacter = (character, quillRef) => {
  const quill = quillRef.current.getEditor();
  const range = quill.getSelection();
  if (range) {
    quill.insertText(range.index, character);
  }
};

// Component to render Spanish characters
const SpanishCharacters = ({ quillRef }) => (
  <div className='spanish-character'>
    {['é', 'á', 'í', 'ó', 'ú', 'ü', 'ñ', '¿', '¡'].map((char) => (
      <button key={char} className='spanish-char-btn' onClick={() => insertSpecialCharacter(char, quillRef)}>
        {char}
      </button>
    ))}
  </div>
);

const ToolBar = ({ toolbarId, spanishShow, completedActivity, setShowSpanishCharacters, showSpanishCharacters, shortLongAnswers, questionIndex, activity, quillRef }) => {
  // Safely get the first answer object (or null if none)
  const firstAnswer = activity?.activityAnswers?.length ? activity.activityAnswers[0] : null;

  // Safely get the specific graded value if exists
  const answerObj = firstAnswer?.answers && firstAnswer.answers.length > questionIndex ? firstAnswer.answers[questionIndex] : null;

  const isSubmitted = firstAnswer?.isSubmitted || false;
  const grade = answerObj?.grade;
  const isGraded = answerObj?.isGraded;

  return (
    <div
      id={toolbarId}
      style={{
        flexDirection: 'column',
        gap: '4px',
        background: '#fff',
        display: spanishShow ? 'flex' : 'none',
      }}
    >
      <div className='toolbar-buttons'>
        <button className='ql-bold' />
        <button className='ql-italic' />
        <button className='ql-underline' />
        <button
          className='ql-clean'
          onClick={(e) => {
            e.preventDefault();
            const quill = quillRef.current.getEditor();
            const selection = quill.getSelection();
            if (selection && selection.length > 0) {
              quill.removeFormat(selection.index, selection.length);
              quill.setSelection(selection.index, 0);
            }
          }}
        />

        <button
          className='omega-btn'
          style={{ display: 'flex' }}
          onClick={() => {
            if (!completedActivity) {
              setShowSpanishCharacters(!showSpanishCharacters);
            }
          }}
        >
          <img src={showSpanishCharacters ? omegaGray : omega} alt='Special Characters' className='omega-img' />
        </button>

        {/* Show grade if available */}
        {shortLongAnswers.length > 0 && typeof grade === 'number' && (
          <div className='saved-correct'>
            <div>
              <span>{Math.round(grade)}%</span>
            </div>
          </div>
        )}

        {/* Show "To be graded" if not graded but submitted */}
        {!isGraded && isSubmitted && (
          <div className='saved-correct'>
            <div>
              <span>To be graded</span>
            </div>
          </div>
        )}
      </div>

      {showSpanishCharacters && <SpanishCharacters quillRef={quillRef} />}
    </div>
  );
};

const EditorInput = ({ answers, questionIndex, editorValue, setAnswer, setAnswerModified, isRead, activity, spanishShow, question = {}, percentageAndDate, hasCorrectAnswers, completedActivity, questions, answersRead, mode }) => {
  const quillRef = useRef(null);
  const [showSpanishCharacters, setShowSpanishCharacters] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [canEdit, setCanEdit] = useState(true); // State to control editing
  const toolbarId = `toolbar-${questionIndex}`;

  const modules = {
    toolbar: {
      container: `#${toolbarId}`, // Use the unique toolbar ID
    },
  };
  const formats = ['bold', 'italic', 'underline', 'clean'];
  const questionId = question._id;

  const getAnswerText = (answers, index) => {
    if (!answers || !questionId || !question.type) return ''; // Safeguard against undefined values
    const answerObj = answers.find((answer) => answer.question === questionId && answer.type === question.type);
    return answerObj
      ? Array.isArray(answerObj.answerText)
        ? answerObj.answerText.join(' ')
        : answerObj.answerText || '' // Ensure answerText is not undefined
      : '';
  };

  const editorContent = getAnswerText(answers, questionIndex) || editorValue || '';

  const shortLongAnswers = activity?.activityAnswers?.[0]?.answers?.filter((answer) => answer.type === 'fr' || answer.type === 'sa') || [];
  const wrodsinSubmit =
    typeof editorContent === 'string' && editorContent !== ''
      ? editorContent
          .replace(/(<([^>]+)>)/gi, '')
          .trim()
          .split(/\s+/)
      : [];

  const answersReadActivty = (answersRead || '')
    .replace(/(<([^>]+)>)/gi, '')
    .trim()
    .split(/\s+/);

  const currentWordCountAPI = wrodsinSubmit.filter(Boolean).length; // Ignore empty strings
  const answersReadright = answersReadActivty.filter(Boolean).length; // Ignore empty strings

  const handleEditorChange = (content) => {
    const text = (content || '').replace(/(<([^>]+)>)/gi, ''); // Handle undefined content safely
    const wordsArray = text.trim().split(/\s+/);
    const currentWordCount = wordsArray.filter(Boolean).length;

    const wordLimit = question?.type === 'fr' ? 110 : 55;

    if (currentWordCount <= wordLimit) {
      setWordCount(currentWordCount);
      setAnswer(questionIndex, 'answerText', content);
      setAnswerModified(true);
    } else {
      const quill = quillRef.current.getEditor();
      quill.setContents(quill.clipboard.convert(content));
    }
  };

  return (
    <div className='editor-container'>
      <div className='omega_text_editor'>
        <ReactQuill
          ref={quillRef}
          theme='snow'
          className={`omega_text_editor-child ${['blanks'].includes(question?.type) ? 'custom-height omega_blanks' : ''} ${['fr'].includes(question?.type) && 'long-res'}`}
          value={mode === 'assign' ? answersRead : mode === 'focused' ? editorContent : ''}
          placeholder='Response goes here'
          readOnly={mode === 'assign' || !canEdit} // Disable editing based on word limit
          onChange={handleEditorChange}
          modules={modules}
          formats={formats}
        />
        <div className='omega_text_counter'>{wordCount || currentWordCountAPI || answersReadright} words</div>
      </div>

      <ToolBar
        toolbarId={toolbarId} // Pass unique toolbar ID to ToolBar
        spanishShow={spanishShow}
        questionIndex={questionIndex}
        completedActivity={completedActivity}
        setShowSpanishCharacters={setShowSpanishCharacters}
        showSpanishCharacters={showSpanishCharacters}
        shortLongAnswers={shortLongAnswers}
        activity={activity}
        quillRef={quillRef}
      />
    </div>
  );
};

export default EditorInput;
